import { Card, Input } from 'antd'
import {
  COMMENT_INDICATOR_GUTTER,
  CommentIndicator,
} from 'features/libra/comments/CommentIndicator'
import { InputProvenanceRow } from 'features/libra/components/InputProvenanceRow'
import { SaveGuardModal } from 'features/libra/SaveGuardModal'
import { useReportValidation } from 'features/libra/StepValidationContext'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { useActiveStepId } from 'features/libra/ticket/TicketStateContext'
import { useRegisterSaveGuard } from 'features/libra/useRegisterSaveGuard'
import {
  LibraWorflowStepComponentProps,
  StepValidationResult,
  UseValidateConfigHook,
} from 'features/libra/workflow/workflowStepsApi'
import capitalize from 'lodash/capitalize'
import { AlertTriangle, Layers } from 'lucide-react'
import type { Dispatch, FC, SetStateAction } from 'react'
import { useCallback, useMemo } from 'react'
import styled from 'styled-components'

import {
  NiiSaveBanner,
  StickySaveBannerContainer,
} from './niiReport/NiiSaveBanner'
import { ZoneAnalysisReviewPanel } from './niiReview/ZoneAnalysisReviewPanel'
import { NiiZones } from './zoneAnalysis/components/NiiZones'
import { ZoneBreakdownAiPrefillButton } from './zoneAnalysis/components/ZoneTicketAiPrefillActions'
import { getCofModifier } from './zoneAnalysis/niiStrategy'
import {
  parseZonesFromFieldValues,
  ZONE_FIELD_IDS,
  zoneAnalysisConfigSchema,
} from './zoneAnalysis/zoneAnalysisApi'
import {
  useZoneAnalysisData,
  ZoneAnalysisDataProvider,
} from './zoneAnalysis/ZoneAnalysisDataProvider'
import { listUnjustifiedOverrideLabels } from './zoneAnalysis/zoneOverrides'

const { TextArea } = Input

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`

const ZoneSaveBannerContainer = styled(StickySaveBannerContainer)`
  margin-right: ${COMMENT_INDICATOR_GUTTER};
`

const SectionCard = styled(Card)`
  margin-bottom: 0;
  border: 2px solid ${({ theme }) => theme.forecast.border};
  background: ${({ theme }) => theme.forecast.background1};

  .ant-card-head {
    padding-inline: 12px;
    background: ${({ theme }) => theme.forecast.background2};
    border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
  }

  .ant-card-head-wrapper {
    gap: 8px;
  }

  .ant-card-head-title {
    min-width: 0;
    font-size: 14px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ant-card-extra {
    flex: 0 0 auto;
    margin-inline-start: auto;
  }
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const HelpText = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.forecast.tertiaryText};
  font-style: italic;
`

const SectionTitle = styled.span`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;

  svg {
    flex: 0 0 auto;
  }
`

const SectionTitleText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ZoneAnalysisContent: FC<{
  setValidationResult: Dispatch<SetStateAction<StepValidationResult>>
  readonly?: boolean
}> = ({ readonly }) => {
  const stepId = useActiveStepId()
  const {
    hasChanges,
    hasPendingChanges,
    isSaving,
    resetChanges,
    doSave,
    isLoading,
    cof,
    zoneBreakdownJustification,
    setZoneBreakdownJustification,
    applyAiZoneBreakdownJustification,
    zones,
    config,
  } = useZoneAnalysisData()
  const { fieldValues } = useTicketAccess()

  const canProceedAfterSave = useCallback(
    () => !hasPendingChanges(),
    [hasPendingChanges]
  )
  const hasUnsavedEdits = !readonly && (hasChanges || isSaving)
  const saveGuard = useRegisterSaveGuard({
    hasUnsavedEdits,
    save: doSave,
    canProceed: canProceedAfterSave,
  })
  const { onSaveAndContinue } = saveGuard
  const handleSaveAndContinue = useCallback(
    () =>
      onSaveAndContinue().catch(() => {
        // The provider reports persistence failures; the guard keeps navigation pending.
      }),
    [onSaveAndContinue]
  )
  const handleSavePage = useCallback(
    () =>
      doSave().catch(() => {
        // The provider reports persistence failures.
      }),
    [doSave]
  )
  const savedZones = useMemo(
    () => parseZonesFromFieldValues(fieldValues, config.zoneFieldName).zones,
    [fieldValues, config.zoneFieldName]
  )
  const draftUnjustified = listUnjustifiedOverrideLabels(zones)
  // Discard & Continue advances with the saved zones, so they must pass too.
  const savedUnjustified = listUnjustifiedOverrideLabels(savedZones)
  useReportValidation(
    draftUnjustified.length > 0
      ? {
          status: 'Error',
          message: `Justify each override before continuing: ${draftUnjustified.join(', ')}`,
        }
      : savedUnjustified.length > 0
        ? {
            status: 'Error',
            message: 'Save the override justifications before continuing.',
          }
        : undefined
  )

  return (
    <Container>
      <SaveGuardModal
        {...saveGuard}
        onSaveAndContinue={handleSaveAndContinue}
        saveDisabled={isSaving}
        saveDisabledReason='Wait for the current zone save to finish before saving page changes.'
      />
      {!readonly && (
        <ZoneSaveBannerContainer>
          <NiiSaveBanner
            hasChanges={hasChanges}
            isLoading={isLoading}
            saveDisabled={isSaving}
            saveDisabledReason='Wait for the current zone save to finish before saving page changes.'
            onReset={resetChanges}
            onSave={handleSavePage}
          />
        </ZoneSaveBannerContainer>
      )}
      <SectionCard
        title={
          <SectionTitle>
            <AlertTriangle size={18} />
            <SectionTitleText>Consequence of Failure</SectionTitleText>
          </SectionTitle>
        }>
        <InputProvenanceRow
          symbol='CoF'
          value={`${capitalize(cof.level)} · ${getCofModifier(cof.level)}×`}
          provenance={{ kind: 'constant', note: 'from Data Gathering' }}
          description='Applies to every zone and scales the required inspection coverage. Edit it in the Data Gathering step.'
        />
      </SectionCard>
      <CommentIndicator
        anchor={{
          type: 'field',
          stepId,
          fieldId: ZONE_FIELD_IDS.BREAKDOWN_JUSTIFICATION,
        }}>
        <SectionCard
          title={
            <SectionTitle>
              <Layers size={18} />
              <SectionTitleText>Zone Breakdown Justification</SectionTitleText>
            </SectionTitle>
          }
          extra={
            <ZoneBreakdownAiPrefillButton
              onApply={applyAiZoneBreakdownJustification}
              readonly={readonly}
            />
          }>
          <FieldGroup>
            <HelpText>
              Explain why the asset was divided into zones in this way — e.g.,
              differences in material, geometry, damage mechanisms, operating
              environment, or inspection access between areas.
            </HelpText>
            <TextArea
              value={zoneBreakdownJustification ?? ''}
              onChange={(e) =>
                setZoneBreakdownJustification(e.target.value || undefined)
              }
              disabled={readonly}
              placeholder='e.g., The vessel was split into three zones to reflect distinct operating environments: the bottom zone experiences liquid phase contact with higher corrosion rates, the shell zone is in vapour phase, and the top head has separate access constraints requiring different inspection methods.'
              rows={4}
            />
          </FieldGroup>
        </SectionCard>
      </CommentIndicator>
      <NiiZones />
    </Container>
  )
}

export const useValidateConfig: UseValidateConfigHook = ({ config }) => {
  return useMemo(
    () => zoneAnalysisConfigSchema.safeParse(config).success,
    [config]
  )
}

export const Component: FC<
  LibraWorflowStepComponentProps<Record<string, unknown>>
> = ({ config, setValidationResult, readonly }) => {
  if (readonly) {
    return <ZoneAnalysisReviewPanel config={config} />
  }

  const parsedConfig = zoneAnalysisConfigSchema.parse(config)
  return (
    <ZoneAnalysisDataProvider config={parsedConfig}>
      <ZoneAnalysisContent
        setValidationResult={setValidationResult}
        readonly={readonly}
      />
    </ZoneAnalysisDataProvider>
  )
}
